/**
 * TripShare.js — Standalone shareable itinerary page
 * Route: /trip-share?d=<base64-encoded-payload>
 *
 * Shows: full itinerary, route, hotel, cost breakdown
 * CTA:   "Back to AI Planner" → /trip-planner?restore=<same-d>
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { stashHotelHandoff, buildHotelBookingUrl } from "./pages/HotelBookingPage";
import UserMenu from "./components/UserMenu";
import Logo from "./components/common/Logo";

const PURPLE = "#6B3FA0";
const ORANGE = "#F4560A";
const GREEN = "#00A86B";
const AMBER = "#F59E0B";
const RED = "#E8334A";

/* ── helpers ── */

// Decompress gzip base64url → original string (mirrors TripPlanner compressToBase64)
async function decompressB64(b64url) {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  const ds = new DecompressionStream("gzip");
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();
  const chunks = [];
  const reader = ds.readable.getReader();
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const total = chunks.reduce((a, c) => a + c.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) { result.set(c, offset); offset += c.length; }
  return new TextDecoder().decode(result);
}

// Unpack: try gzip first, fall back to plain base64
async function unpackTrip(encoded) {
  // Try gzip decompression (H4sI... prefix = gzip magic bytes in base64)
  try {
    const json = await decompressB64(encoded);
    return JSON.parse(json);
  } catch (_) { }
  // Fall back to plain base64 (legacy links)
  try {
    return JSON.parse(decodeURIComponent(escape(atob(encoded))));
  } catch (e) {
    console.error("[TripShare] unpack failed:", e);
    return null;
  }
}

const SHORT_LINK_API_TS = "https://f2qq5dfp52.execute-api.ap-south-1.amazonaws.com/default/create_short_link";

async function getShortLink(longUrl) {
  try {
    const resp = await fetch(SHORT_LINK_API_TS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "trip", url: longUrl }),
    });
    if (!resp.ok) throw new Error(`${resp.status}`);
    const data = await resp.json();
    const payload = data.data || data;
    const short = payload.short_url || payload.shortUrl || payload.url || payload.link || payload.id
      || data.short_url || data.shortUrl;
    if (short) return short.startsWith("http") ? short : `https://routechef.com/s/${short}`;
    throw new Error("no url in response");
  } catch (e) {
    console.warn("[TripShare] short link failed:", e.message);
    return longUrl;
  }
}

// Extract km from origin_comment / dest_comment and build a cab label
function cabInfo(comment) {
  if (!comment) return null;
  const m = comment.match(/(\d+(?:\.\d+)?)\s*km/i);
  return m ? `🚕 ${m[1]} km by cab` : null;
}

function fmtDur(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function depTime(seg) {
  return seg?.origin_dep_dt
    ? new Date(seg.origin_dep_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "";
}
function arrTime(seg) {
  return seg?.dest_arr_dt
    ? new Date(seg.dest_arr_dt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "";
}

/* ── place image map ── */
const IMGS = {
  hill: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&q=70",
  beach: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&q=70",
  city: "https://images.unsplash.com/photo-1529253355930-ddbe423a2ac7?w=400&q=70",
};
function placeImg(type = "") {
  const t = (type || "").toLowerCase();
  if (t.includes("beach") || t.includes("coast")) return IMGS.beach;
  if (t.includes("hill") || t.includes("mountain") || t.includes("station")) return IMGS.hill;
  return IMGS.city;
}

/* ── TL helpers ── */
const Dot = ({ color, last }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 22, flexShrink: 0 }}>
    <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, border: `2px solid ${color}`, marginTop: 4 }} />
    {!last && <div style={{ width: 2, flex: 1, minHeight: 16, background: color + "40", margin: "3px 0" }} />}
  </div>
);

/* ── MAIN COMPONENT ── */
export default function TripShare() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const encoded = params.get("d");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandDay, setExpandDay] = useState(null);
  const [copyState, setCopyState] = useState("idle"); // "idle"|"loading"|"copied"
  const [taxOpen, setTaxOpen] = useState(false);
  const [pickedHotel, setPickedHotel] = useState(null); // for edit — user picks different hotel

  // Async decode on mount
  useEffect(() => {
    if (!encoded) { setLoading(false); return; }
    unpackTrip(encoded).then(d => { setData(d); setLoading(false); });
  }, [encoded]);

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter',-apple-system,sans-serif", background: "#fff"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗺️</div>
          <div style={{ fontSize: 14, color: "#888" }}>Loading itinerary…</div>
        </div>
      </div>
    );
  }

  if (!data || !data.tripState) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        fontFamily: "'Inter',-apple-system,sans-serif",
        background: "#fff", gap: 16, padding: 32,
      }}>
        <style>@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');</style>
        <div style={{ fontSize: 48 }}>🗺️</div>
        <div style={{ fontWeight: 700, fontSize: 20, color: "#333" }}>Trip not found</div>
        <div style={{ fontSize: 14, color: "#888", textAlign: "center", maxWidth: 300, lineHeight: 1.6 }}>
          This link may have expired or been corrupted. Ask your friend to share it again.
        </div>
        <a href="/trip-planner" style={{
          padding: "12px 28px", background: ORANGE, borderRadius: 99,
          color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none",
        }}>Start planning →</a>
      </div>
    );
  }

  const { tripState, tripInfo, route, returnRoute, hotel, nights, allHotels: shareHotels } = data;
  const dest = tripState.primary_destination || tripInfo?.destination || "Trip";
  const source = tripInfo?.source || "Home";
  const days = tripState.itinerary || [];
  const places = tripState.selected_places || tripState.all_places?.filter(p =>
    tripState.selected_place_ids?.includes(p.id)) || [];

  const hRoom = hotel?.selected_room;
  // Graceful fallback for old share links that don't have selected_room
  const hTotalFare = hRoom?.total_fare || hRoom?.TotalFare
    || (hotel?.price_per_night_inr ? hotel.price_per_night_inr * (nights || 1) : 0);
  const hTotalTax = hRoom?.total_tax || hRoom?.TotalTax || 0;
  const hPlatFee = hotel ? 249 : 0;
  const hGST = hotel ? Math.round(249 * 0.18) : 0;
  const hGrandTotal = hTotalFare + hTotalTax + hPlatFee + hGST;
  const roomName = hRoom?.name?.split(",")[0] || "";
  const hotelPrice = hTotalFare;
  const routePrice = typeof route?.price === "string" ? Number(route.price) : (route?.price || 0);
  const returnRoutePrice = typeof returnRoute?.price === "string" ? Number(returnRoute.price) : (returnRoute?.price || 0);
  const total = routePrice + returnRoutePrice + hGrandTotal;

  // Compute hotel check_in from route arrival, check_out = check_in + nights
  const hotelCheckIn = (() => {
    const arrIso = route?.rawRoute?.nodes?.slice(-1)[0]?.segment?.dest_arr_dt;
    if (arrIso) return new Date(arrIso).toISOString().split("T")[0];
    if (tripInfo?.startDate) {
      const [d, m, y] = tripInfo.startDate.split("-");
      return `${y}-${m}-${d}`;
    }
    return "";
  })();
  const hotelCheckOut = (() => {
    if (!hotelCheckIn) return "";
    const co = new Date(hotelCheckIn);
    co.setDate(co.getDate() + (nights || 1));
    return co.toISOString().split("T")[0];
  })();

  // Navigate to the hotel's own booking page instead of opening a modal —
  // gives it a real URL that survives refresh and is bookmarkable.
  const handleBookHotel = () => {
    const target = pickedHotel || hotel;
    if (!target?.hotel_code) return;
    stashHotelHandoff(target.hotel_code, {
      ...target,
      rooms: (target.rooms || hotel?.rooms || []).filter(r => r.booking_code || r.BookingCode),
      check_in: hotelCheckIn,
      check_out: hotelCheckOut,
      userId: data?.userId || "",
    });
    navigate(buildHotelBookingUrl(target.hotel_code, {
      checkIn: hotelCheckIn, checkOut: hotelCheckOut, userId: data?.userId || "",
    }));
  };

  const fmtP = (n) => {
    if (!n && n !== 0) return "";
    const num = Number(n);
    if (isNaN(num)) return "";
    const hasDecimal = (num % 1) !== 0;
    return num.toLocaleString("en-IN", { minimumFractionDigits: hasDecimal ? 2 : 0, maximumFractionDigits: 2 });
  };

  // Outbound route legs
  const rawRoute = route?.rawRoute;
  const nodes = rawRoute?.nodes || [];
  const firstSeg = nodes[0]?.segment;
  const lastSeg = nodes[nodes.length - 1]?.segment;

  // Return route legs
  const rawReturnRoute = returnRoute?.rawRoute;
  const returnNodes = rawReturnRoute?.nodes || [];
  const firstReturnSeg = returnNodes[0]?.segment;
  const lastReturnSeg = returnNodes[returnNodes.length - 1]?.segment;

  const handleCopyLink = async () => {
    if (copyState === "loading") return;
    setCopyState("loading");
    const shortUrl = await getShortLink(window.location.href);
    try {
      await navigator.clipboard.writeText(shortUrl);
    } catch (_) { }
    setCopyState("copied");
    setTimeout(() => setCopyState("idle"), 2500);
  };

  const plannerUrl = `/trip-planner?restore=${encoded}`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        html,body{overscroll-behavior:none;background:#fff;height:100%;overflow:hidden}
        body{font-family:'Inter',-apple-system,sans-serif;background:#fff;color:#111}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-thumb{background:#ddd;border-radius:99px}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        .fi{animation:fadeUp .35s ease both}
      `}</style>

      <div style={{
        position: "fixed", inset: 0,
        display: "flex", flexDirection: "column",
        overflowX: "hidden", background: "#fff",
      }}>

        {/* ── Nav ── */}
        <nav style={{
          background: "#fff", borderBottom: "1px solid #F0F0F0",
          padding: "14px 28px", display: "flex", alignItems: "center",
          justifyContent: "space-between", flexShrink: 0,
        }}>
          {/* Logo — shared component, identical across the whole site */}
          <Logo />

          {/* Account — identical pattern to TripPlanner/HotelBooking */}
          <UserMenu />
        </nav>

        {/* ── Scrollable content (hero + body) ── */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>

          {/* ── Hero banner ── */}
          <div style={{
            background: `linear-gradient(135deg,#F4560A 0%,#E8450A 50%,#F7931E 100%)`,
            padding: "28px 24px 24px",
            position: "relative", overflow: "hidden",
          }}>
            {/* Background texture circles */}
            <div style={{
              position: "absolute", top: -40, right: -40, width: 180, height: 180,
              borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none"
            }} />
            <div style={{
              position: "absolute", bottom: -30, left: -30, width: 120, height: 120,
              borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none"
            }} />
            {/* Ticket punch holes */}
            {[-1, 1].map(side => (
              <div key={side} style={{
                position: "absolute",
                [side === -1 ? "left" : "right"]: -12,
                top: "50%", transform: "translateY(-50%)",
                width: 24, height: 24, borderRadius: "50%", background: "#fff",
              }} />
            ))}

            <div style={{ maxWidth: 640, margin: "0 auto", position: "relative" }}>
              <div style={{
                fontSize: 9, color: "rgba(255,255,255,0.6)", fontWeight: 700,
                letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 14
              }}>✦ Shared Trip Plan</div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 16 }}>
                <div>
                  {/* Full place names with arrow */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div>
                      <div style={{
                        fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 700,
                        letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 2
                      }}>From</div>
                      <div style={{
                        fontWeight: 900, fontSize: 26, color: "#fff", lineHeight: 1.1,
                        textShadow: "0 2px 8px rgba(0,0,0,0.15)"
                      }}>
                        {source}
                      </div>
                    </div>
                    <div style={{
                      color: "rgba(255,255,255,0.6)", fontSize: 20, fontWeight: 300,
                      margin: "0 4px", paddingTop: 18
                    }}>→</div>
                    <div>
                      <div style={{
                        fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 700,
                        letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 2
                      }}>To</div>
                      <div style={{
                        fontWeight: 900, fontSize: 26, color: "#fff", lineHeight: 1.1,
                        textShadow: "0 2px 8px rgba(0,0,0,0.15)"
                      }}>
                        {dest}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                    {[
                      `📅 ${nights} nights`,
                      `📍 ${places.length} places`,
                      ...(route ? [`${route.icon || "🚂"} ${route.label}`] : []),
                      ...(hotel ? [`🏨 Stay included`] : []),
                    ].map(t => (
                      <span key={t} style={{
                        fontSize: 11, color: "#fff", fontWeight: 600,
                        background: "rgba(0,0,0,0.18)", borderRadius: 99, padding: "4px 12px",
                        backdropFilter: "blur(4px)"
                      }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Total */}
                {total > 0 && (
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{
                      fontSize: 9, color: "rgba(255,255,255,0.55)", letterSpacing: 1.5,
                      textTransform: "uppercase", marginBottom: 4
                    }}>Est. Total</div>
                    <div style={{
                      fontWeight: 900, fontSize: 36, color: "#fff", lineHeight: 1,
                      textShadow: "0 2px 12px rgba(0,0,0,0.2)"
                    }}>
                      ₹{fmtP(total)}
                    </div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>per person</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ maxWidth: 640, margin: "0 auto", width: "100%", padding: "24px 16px 60px" }}>

            {/* Outbound route legs card */}
            {nodes.length > 0 && (
              <div className="fi" style={{
                background: "#fff", borderRadius: 16, border: "1px solid #E8E8E8",
                overflow: "hidden", marginBottom: 20,
              }}>
                <div style={{
                  padding: "12px 16px", borderBottom: "1px solid #F0F0F0",
                  display: "flex", alignItems: "center", gap: 8
                }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>🗺️ Outbound Journey</span>
                  {firstSeg && lastSeg && (
                    <span style={{ fontSize: 11, color: "#888" }}>
                      {depTime(firstSeg)} → {arrTime(lastSeg)}
                    </span>
                  )}
                </div>
                <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Origin cab strip */}
                  {cabInfo(nodes[0]?.segment?.origin_comment || nodes[0]?.segment?.origin_comment_web) && (
                    <div style={{
                      fontSize: 10.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 7, padding: "4px 10px", display: "inline-block"
                    }}>
                      {cabInfo(nodes[0]?.segment?.origin_comment || nodes[0]?.segment?.origin_comment_web)}
                      <span style={{ color: "#bbb", marginLeft: 4 }}>· to departure point</span>
                    </div>
                  )}
                  {nodes.map((node, ni) => {
                    const seg = node.segment;
                    const isFlight = !!seg.carrier;
                    const avl = seg.seat_avail_ui;
                    const legSrc = seg.origin_name || seg.origin_code_iata || seg.origin_code || "";
                    const legDest = seg.dest_name || seg.dest_code_iata || seg.dest_code || "";
                    const stop = rawRoute?.stops_data?.[ni];
                    return (
                      <div key={node.node_id || ni}>
                        <div style={{
                          background: isFlight ? "#F5F0FF" : "#F0F9F4",
                          borderRadius: 10, padding: "10px 12px",
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>{isFlight ? "✈️" : "🚂"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#111", marginBottom: 3 }}>
                              {isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} (${seg.train_num})`}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, color: "#aaa" }}>
                                  {seg.origin_dep_dt && new Date(seg.origin_dep_dt).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 12, color: "#333" }}>{depTime(seg)}</span>
                              </div>
                              <div style={{ flex: 0, fontSize: 11, color: "#bbb" }}>→</div>
                              <div style={{ flex: 1, textAlign: "right" }}>
                                <div style={{ fontSize: 10, color: "#aaa" }}>
                                  {seg.dest_arr_dt && new Date(seg.dest_arr_dt).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 12, color: "#333" }}>{arrTime(seg)}</span>
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                              {legSrc} → {legDest}
                              <span style={{ marginLeft: 6, color: "#bbb" }}>{fmtDur(seg.duration_mins)}</span>
                              {seg.seat_class_ui && (
                                <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>
                                  {seg.seat_class_ui}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                            <div style={{
                              display: "flex", alignItems: "center", gap: 3,
                              background: avl?.is_avail ? GREEN + "18" : "#F5F5F5",
                              borderRadius: 99, padding: "3px 9px",
                            }}>
                              <div style={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: avl?.is_avail ? GREEN : "#ccc"
                              }} />
                              <span style={{ fontSize: 10, fontWeight: 700, color: avl?.is_avail ? GREEN : "#999" }}>
                                {avl?.is_avail ? "Available" : "Check"}
                              </span>
                            </div>
                            {avl?.fare && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>₹{Math.round(avl.fare).toLocaleString()}</span>
                            )}
                            {seg.booking_link && (
                              <a href={seg.booking_link} target="_blank" rel="noopener noreferrer"
                                style={{
                                  fontSize: 11, fontWeight: 700, color: "#fff",
                                  background: isFlight ? PURPLE : GREEN,
                                  borderRadius: 99, padding: "4px 12px",
                                  textDecoration: "none", whiteSpace: "nowrap"
                                }}>
                                {isFlight ? "✈️ Book" : "🎫 Book"}
                              </a>
                            )}
                          </div>
                        </div>
                        {stop && ni < nodes.length - 1 && (
                          <div style={{
                            margin: "6px 0 0 32px", fontSize: 10.5, color: "#92400E",
                            background: "#FFFBEB", borderRadius: 7, padding: "4px 10px", display: "inline-block"
                          }}>
                            🔄 {stop.stop_title} · {stop.commute_comment}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Destination cab strip */}
                  {cabInfo(nodes[nodes.length - 1]?.segment?.dest_comment || nodes[nodes.length - 1]?.segment?.dest_comment_web) && (
                    <div style={{
                      fontSize: 10.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 7, padding: "4px 10px", display: "inline-block"
                    }}>
                      {cabInfo(nodes[nodes.length - 1]?.segment?.dest_comment || nodes[nodes.length - 1]?.segment?.dest_comment_web)}
                      <span style={{ color: "#bbb", marginLeft: 4 }}>· to destination</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Return journey card */}
            {returnNodes.length > 0 && (
              <div className="fi" style={{
                background: "#fff", borderRadius: 16, border: "1px solid #E8E8E8",
                overflow: "hidden", marginBottom: 20,
              }}>
                <div style={{
                  padding: "12px 16px", borderBottom: "1px solid #F0F0F0",
                  display: "flex", alignItems: "center", gap: 8
                }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>🔄 Return Journey</span>
                  {firstReturnSeg && lastReturnSeg && (
                    <span style={{ fontSize: 11, color: "#888" }}>
                      {depTime(firstReturnSeg)} → {arrTime(lastReturnSeg)}
                    </span>
                  )}
                  {returnRoute?.price > 0 && (
                    <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: ORANGE }}>
                      ₹{returnRoute.price.toLocaleString()}
                    </span>
                  )}
                </div>
                <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Origin cab strip for return */}
                  {cabInfo(returnNodes[0]?.segment?.origin_comment || returnNodes[0]?.segment?.origin_comment_web) && (
                    <div style={{
                      fontSize: 10.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 7, padding: "4px 10px", display: "inline-block"
                    }}>
                      {cabInfo(returnNodes[0]?.segment?.origin_comment || returnNodes[0]?.segment?.origin_comment_web)}
                      <span style={{ color: "#bbb", marginLeft: 4 }}>· to departure point</span>
                    </div>
                  )}
                  {returnNodes.map((node, ni) => {
                    const seg = node.segment;
                    const isFlight = !!seg.carrier;
                    const avl = seg.seat_avail_ui;
                    const legSrc = seg.origin_name || seg.origin_code_iata || seg.origin_code || "";
                    const legDest = seg.dest_name || seg.dest_code_iata || seg.dest_code || "";
                    const stop = rawReturnRoute?.stops_data?.[ni];
                    return (
                      <div key={node.node_id || ni}>
                        <div style={{
                          background: isFlight ? "#F5F0FF" : "#F0F9F4",
                          borderRadius: 10, padding: "10px 12px",
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                          <span style={{ fontSize: 18, flexShrink: 0 }}>{isFlight ? "✈️" : "🚂"}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: "#111", marginBottom: 3 }}>
                              {isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} (${seg.train_num})`}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 10, color: "#aaa" }}>
                                  {seg.origin_dep_dt && new Date(seg.origin_dep_dt).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 12, color: "#333" }}>{depTime(seg)}</span>
                              </div>
                              <div style={{ flex: 0, fontSize: 11, color: "#bbb" }}>→</div>
                              <div style={{ flex: 1, textAlign: "right" }}>
                                <div style={{ fontSize: 10, color: "#aaa" }}>
                                  {seg.dest_arr_dt && new Date(seg.dest_arr_dt).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
                                </div>
                                <span style={{ fontWeight: 700, fontSize: 12, color: "#333" }}>{arrTime(seg)}</span>
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                              {legSrc} → {legDest}
                              <span style={{ marginLeft: 6, color: "#bbb" }}>{fmtDur(seg.duration_mins)}</span>
                              {seg.seat_class_ui && (
                                <span style={{ marginLeft: 6, color: isFlight ? PURPLE : GREEN, fontWeight: 600 }}>
                                  {seg.seat_class_ui}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5, flexShrink: 0 }}>
                            <div style={{
                              display: "flex", alignItems: "center", gap: 3,
                              background: avl?.is_avail ? GREEN + "18" : "#F5F5F5",
                              borderRadius: 99, padding: "3px 9px",
                            }}>
                              <div style={{
                                width: 6, height: 6, borderRadius: "50%",
                                background: avl?.is_avail ? GREEN : "#ccc"
                              }} />
                              <span style={{ fontSize: 10, fontWeight: 700, color: avl?.is_avail ? GREEN : "#999" }}>
                                {avl?.is_avail ? "Available" : "Check"}
                              </span>
                            </div>
                            {avl?.fare && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>₹{Math.round(avl.fare).toLocaleString()}</span>
                            )}
                            {seg.booking_link && (
                              <a href={seg.booking_link} target="_blank" rel="noopener noreferrer"
                                style={{
                                  fontSize: 11, fontWeight: 700, color: "#fff",
                                  background: isFlight ? PURPLE : GREEN,
                                  borderRadius: 99, padding: "4px 12px",
                                  textDecoration: "none", whiteSpace: "nowrap"
                                }}>
                                {isFlight ? "✈️ Book" : "🎫 Book"}
                              </a>
                            )}
                          </div>
                        </div>
                        {stop && ni < returnNodes.length - 1 && (
                          <div style={{
                            margin: "6px 0 0 32px", fontSize: 10.5, color: "#92400E",
                            background: "#FFFBEB", borderRadius: 7, padding: "4px 10px", display: "inline-block"
                          }}>
                            🔄 {stop.stop_title} · {stop.commute_comment}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Destination cab strip for return */}
                  {cabInfo(returnNodes[returnNodes.length - 1]?.segment?.dest_comment || returnNodes[returnNodes.length - 1]?.segment?.dest_comment_web) && (
                    <div style={{
                      fontSize: 10.5, color: "#92400E", background: "#FFF8F0",
                      borderRadius: 7, padding: "4px 10px", display: "inline-block"
                    }}>
                      {cabInfo(returnNodes[returnNodes.length - 1]?.segment?.dest_comment || returnNodes[returnNodes.length - 1]?.segment?.dest_comment_web)}
                      <span style={{ color: "#bbb", marginLeft: 4 }}>· to destination</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hotel card */}
            {hotel && (
              <div className="fi" style={{
                background: "#fff", borderRadius: 16, border: "1px solid #E8E8E8",
                marginBottom: 20, overflow: "hidden",
              }}>
                {/* Header row — matches route card pattern */}
                <div style={{
                  padding: "12px 16px", borderBottom: "1px solid #F0F0F0",
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10
                }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: "#111" }}>🏨 Accommodation</span>
                  {hTotalFare > 0 && (
                    <span style={{ fontWeight: 800, fontSize: 15, color: ORANGE }}>₹{fmtP(hTotalFare)}</span>
                  )}
                </div>
                {/* Body row */}
                <div style={{ padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 14 }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12, background: "#F0F0F0",
                    flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22
                  }}>
                    {hotel.primary_image
                      ? <img src={hotel.primary_image} alt={hotel.hotel_name}
                        style={{ width: "100%", height: "100%", borderRadius: 12, objectFit: "cover" }}
                        onError={e => { e.target.style.display = "none"; }} />
                      : "🏨"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Name + nights on same row */}
                    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111", lineHeight: 1.3, flex: 1 }}>{hotel.hotel_name}</div>
                      <span style={{ fontSize: 11, color: "#aaa", flexShrink: 0, whiteSpace: "nowrap" }}>{nights} night{nights !== 1 ? "s" : ""}</span>
                    </div>
                    {/* Room type */}
                    {roomName && <div style={{ fontSize: 12, color: "#555", marginTop: 1 }}>{roomName}</div>}
                    {/* Meal + location row */}
                    <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                      {hRoom?.meal_type && hRoom.meal_type !== "Room_Only" ? (
                        <span style={{
                          fontSize: 10, color: "#22C55E", fontWeight: 700,
                          background: "#F0FDF4", borderRadius: 99, padding: "2px 8px"
                        }}>
                          🍳 {hRoom.meal_type === "BreakFast" || hRoom.meal_type === "Breakfast_For_2" ? "Breakfast incl."
                            : hRoom.meal_type === "Half_Board" ? "Half board"
                              : hRoom.meal_type === "Full_Board" ? "Full board"
                                : hRoom.meal_type === "All_Inclusive" ? "All inclusive"
                                  : hRoom.meal_type}
                        </span>
                      ) : hRoom?.meal_type === "Room_Only" ? (
                        <span style={{ fontSize: 10, color: "#888", background: "#F5F5F7", borderRadius: 99, padding: "2px 8px" }}>🛏 Room only</span>
                      ) : null}
                      {hRoom?.is_refundable && (
                        <span style={{ fontSize: 10, color: "#22C55E", fontWeight: 600 }}>✓ Free cancel</span>
                      )}
                      {(hotel.city_name || hotel.hotel_area || hotel.location_label) && (
                        <span style={{ fontSize: 10, color: "#aaa" }}>📍 {hotel.city_name || hotel.hotel_area || hotel.location_label}</span>
                      )}
                    </div>
                    {/* Book Hotel button */}
                    {hotel.hotel_code && (
                      <button onClick={handleBookHotel} style={{
                        marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6,
                        background: ORANGE, color: "#fff", border: "none", borderRadius: 99,
                        padding: "7px 16px", fontFamily: "inherit", fontWeight: 700, fontSize: 12,
                        cursor: "pointer",
                      }}>
                        🏨 Book Hotel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Day-by-day itinerary — redesigned */}
            {days.length > 0 && (
              <div className="fi" style={{ marginBottom: 20 }} id="itinerary-days">
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ fontWeight: 800, fontSize: 17, color: "#111" }}>📅 Day-by-Day Plan</div>
                  <button
                    onClick={() => {
                      // ── Segment row with booking link ────────────────────────
                      const segHtml = (seg, stopRow = "") => {
                        const isFlight = !!seg.carrier;
                        const dep = seg.origin_dep_dt ? new Date(seg.origin_dep_dt) : null;
                        const arr = seg.dest_arr_dt ? new Date(seg.dest_arr_dt) : null;
                        const fmtT = d => d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "—";
                        const fmtD = d => d ? d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }) : "";
                        const fare = seg.seat_avail_ui?.fare;
                        const id = isFlight ? `${seg.carrier} ${seg.flight_num}` : `${(seg.train_name || "").trim()} #${seg.train_num}`;
                        const from = seg.origin_name || seg.origin_code_iata || "";
                        const to = seg.dest_name || seg.dest_code_iata || "";
                        const dur = seg.duration_mins ? `${Math.floor(seg.duration_mins / 60)}h${seg.duration_mins % 60 > 0 ? " " + seg.duration_mins % 60 + "m" : ""}` : "";
                        const bookBtn = seg.booking_link
                          ? `<a href="${seg.booking_link}" style="display:inline-block;font-size:11px;font-weight:700;color:#fff;background:${isFlight ? "#6B3FA0" : "#00A86B"};border-radius:99px;padding:4px 12px;text-decoration:none;white-space:nowrap">${isFlight ? "✈️ Book flight" : "🎫 Book train"}</a>`
                          : "";
                        return `
                        <div style="background:${isFlight ? "#F5F0FF" : "#F0F9F4"};border-radius:9px;padding:10px 12px;margin-bottom:6px;display:flex;align-items:center;gap:10px">
                          <span style="font-size:16px;flex-shrink:0">${isFlight ? "✈️" : "🚂"}</span>
                          <div style="flex:1;min-width:0">
                            <div style="font-weight:700;font-size:13px;color:#111">${id}</div>
                            <div style="font-size:11px;color:#555;margin-top:2px">${from} → ${to}</div>
                            <div style="font-size:10px;color:#888;margin-top:1px">${fmtD(dep)} · ${fmtT(dep)} – ${fmtT(arr)}${seg.seat_class_ui ? " · <b>" + seg.seat_class_ui + "</b>" : ""}${dur ? " · " + dur : ""}</div>
                            ${seg.origin_platform ? `<div style="font-size:10px;color:#bbb;margin-top:1px">Platform ${seg.origin_platform}${seg.dest_platform ? " → " + seg.dest_platform : ""}</div>` : ""}
                          </div>
                          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;flex-shrink:0">
                            ${fare ? `<div style="font-weight:800;font-size:13px;color:#111">₹${Math.round(fare).toLocaleString()}</div>` : ""}
                            ${bookBtn}
                          </div>
                        </div>${stopRow}`;
                      };

                      // ── Route section (purple header) ────────────────────────
                      const routeSection = (rawRoute, label, tag) => {
                        if (!rawRoute?.nodes) return "";
                        const nodes = rawRoute.nodes || [];
                        const stops = rawRoute.stops_data || [];
                        const segs = nodes.map((n, ni) => {
                          const stop = stops.find(s => s.seq === ni + 1);
                          const stopRow = stop && ni < nodes.length - 1
                            ? `<div style="margin:2px 0 5px 28px;font-size:10px;color:#92400E;background:#FFF8F0;border-radius:6px;padding:4px 10px;display:inline-block">🔄 ${stop.stop_title}${stop.commute_comment ? " · " + stop.commute_comment : ""}</div>`
                            : "";
                          return segHtml(n.segment, stopRow);
                        }).join("");
                        return `
                        <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #E8E8E8;overflow:hidden;page-break-inside:avoid">
                          <div style="padding:12px 16px;background:linear-gradient(135deg,#6B3FA0,#9B6DD0);display:flex;align-items:center;gap:10px">
                            <div style="font-weight:800;font-size:14px;color:#fff">${label}</div>
                            <div style="font-size:11px;color:rgba(255,255,255,0.7)">${tag}</div>
                          </div>
                          <div style="padding:12px 14px">${segs}</div>
                        </div>`;
                      };

                      // ── Hotel section (amber header) ─────────────────────────
                      const hotelSection = hotel ? `
                      <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #E8E8E8;overflow:hidden;page-break-inside:avoid">
                        <div style="padding:12px 16px;background:linear-gradient(135deg,#F59E0B,#FCD34D);display:flex;align-items:center;gap:10px">
                          <span style="font-size:18px">🏨</span>
                          <div style="font-weight:800;font-size:14px;color:#fff">Accommodation · ${nights} nights</div>
                        </div>
                        <div style="padding:12px 16px">
                          <div style="font-weight:700;font-size:14px;color:#111">${hotel.hotel_name}</div>
                          ${hotel.city_name ? `<div style="font-size:11px;color:#888;margin-top:2px">${hotel.city_name}</div>` : ""}
                          ${hTotalFare > 0 ? `<div style="font-size:11px;color:#F4560A;font-weight:700;margin-top:4px">₹${fmtP(hTotalFare)} total · ₹${fmtP(hTotalTax + hPlatFee + hGST)} taxes & fees</div>` : ""}
                        </div>
                      </div>` : "";

                      // ── Place row ────────────────────────────────────────────
                      const placeRow = v => {
                        const place = v.place || {};
                        return `
                        <div style="display:flex;gap:10px;margin-bottom:10px;align-items:flex-start">
                          <div style="width:8px;height:8px;border-radius:50%;background:#F4560A;margin-top:5px;flex-shrink:0"></div>
                          <div style="flex:1;background:#FAFAF8;border-radius:8px;padding:10px 12px;border:1px solid #F0EDE8">
                            <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px">
                              <div style="font-weight:700;font-size:13px;color:#111">${place.name || v.place_id || ""}</div>
                              ${(v.start_time || v.end_time) ? `<div style="font-size:10px;color:#fff;background:#F4560A;border-radius:99px;padding:2px 8px;white-space:nowrap;flex-shrink:0">${v.start_time || ""}${v.end_time ? "–" + v.end_time : ""}</div>` : ""}
                            </div>
                            ${place.type ? `<div style="font-size:10px;color:#E8450A;margin-top:3px;font-weight:600">${place.type}</div>` : ""}
                            ${v.travel_from_prev && v.travel_from_prev !== "Start of day" ? `<div style="font-size:10px;color:#bbb;margin-top:3px">🚗 ${v.travel_from_prev}</div>` : ""}
                            ${place.insider_tip ? `<div style="font-size:10px;color:#555;margin-top:6px;background:#FFF4EE;border-radius:6px;padding:5px 8px;border-left:3px solid #F4560A">💡 ${place.insider_tip.length > 120 ? place.insider_tip.slice(0, 120) + "…" : place.insider_tip}</div>` : ""}
                          </div>
                        </div>`;
                      };

                      // ── Day sections (hotel inserted after day 1) ────────────
                      const daySections = days.map((day, di) => {
                        const visits = day.place_visits || [];
                        const visitRows = visits.map(placeRow).join("");
                        const hotelInsert = di === 0 && hotel ? hotelSection : "";
                        return `
                        <div style="margin-bottom:20px;background:#fff;border-radius:12px;border:1px solid #F0EDE8;overflow:hidden;page-break-inside:avoid">
                          <div style="padding:12px 16px;background:linear-gradient(135deg,#FFF4EE,#FFF8F0);border-bottom:1px solid #F0EDE8;display:flex;align-items:center;gap:12px">
                            <div style="width:32px;height:32px;border-radius:10px;background:linear-gradient(135deg,#F4560A,#F7931E);display:flex;align-items:center;justify-content:center;font-weight:900;font-size:14px;color:#fff;flex-shrink:0">${di + 1}</div>
                            <div>
                              <div style="font-weight:700;font-size:13px;color:#111">Day ${day.day_number}${day.area_summary ? " · " + day.area_summary : ""}</div>
                              <div style="font-size:11px;color:#aaa">${visits.length} place${visits.length !== 1 ? "s" : ""}</div>
                            </div>
                          </div>
                          ${visits.length > 0 ? `<div style="padding:12px 14px">${visitRows}</div>` : `<div style="padding:12px 16px;color:#bbb;font-size:12px">Free day — explore at your own pace</div>`}
                          ${day.day_notes ? `<div style="margin:0 14px 12px;font-size:11px;color:#888;background:#F5F5F7;border-radius:7px;padding:6px 10px">ℹ️ ${day.day_notes}</div>` : ""}
                        </div>${hotelInsert}`;
                      }).join("");

                      // ── Cost breakdown ───────────────────────────────────────
                      const costHtml = total > 0 ? `
                      <div style="background:#fff;border-radius:12px;border:1px solid #F0EDE8;overflow:hidden;margin-bottom:20px">
                        <div style="height:4px;background:linear-gradient(90deg,#F4560A,#E8450A,#F7931E)"></div>
                        <div style="padding:14px 16px">
                          <div style="font-size:10px;font-weight:700;color:#999;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:10px">Cost Breakdown</div>
                          ${route ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">${route.icon || "🚂"} ${route.label} <span style="font-size:10px;color:#aaa">outbound</span></span><span style="font-size:13px;font-weight:700">${route.price > 0 ? "₹" + route.price.toLocaleString() : "—"}</span></div>` : ""}
                          ${returnRoute && returnRoute.price > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">${returnRoute.icon || "🔄"} ${returnRoute.label} <span style="font-size:10px;color:#aaa">return</span></span><span style="font-size:13px;font-weight:700">₹${returnRoute.price.toLocaleString()}</span></div>` : ""}
                          ${hotel && hTotalFare > 0 ? `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#444">🏨 ${hotel.hotel_name?.split(" ").slice(0, 3).join(" ") || ""} <span style="font-size:10px;color:#aaa">${nights}n</span></span><span style="font-size:13px;font-weight:700">₹${fmtP(hTotalFare)}</span></div><div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #F0F0F0"><span style="font-size:12px;color:#888">🧾 Taxes &amp; fees</span><span style="font-size:12px;font-weight:600;color:#666">₹${fmtP(hTotalTax + hPlatFee + hGST)}</span></div>` : ""}
                          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:10px;padding:10px 12px;background:#FFF4EE;border-radius:8px;border:1px solid #FFE0CC">
                            <div>
                              <div style="font-size:11px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:1px">Estimated Total</div>
                              <div style="font-size:10px;color:#bbb">per person · excl. activities</div>
                            </div>
                            <div style="font-size:26px;font-weight:900;color:#F4560A">₹${total.toLocaleString()}</div>
                          </div>
                        </div>
                      </div>` : "";

                      // ── Assemble: outbound → days+hotel → return → cost ──────
                      const html = `<!DOCTYPE html><html><head>
                      <meta charset="utf-8"/>
                      <title>${dest} Itinerary</title>
                      <style>
                        *{box-sizing:border-box;margin:0;padding:0}
                        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;color:#111;padding:28px 32px}
                        a{color:inherit}
                        @media print{body{padding:16px}@page{margin:12mm}}
                      </style>
                    </head><body>
                      <div style="margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #F4560A">
                        <div style="font-size:22px;font-weight:900;color:#111;margin-bottom:4px">${(tripInfo?.source || "Home").toUpperCase()} → ${dest.toUpperCase()}</div>
                        <div style="font-size:13px;color:#888">${nights} nights · ${days.length} days · ${(tripState?.selected_place_ids || []).length} places${tripInfo?.startDate ? " · From " + tripInfo.startDate : ""}</div>
                        <div style="font-size:11px;color:#bbb;margin-top:4px">Generated by RouteChef AI · routechef.com</div>
                      </div>

                      ${route?.rawRoute ? routeSection(route.rawRoute, `${route.icon || "🚂"} ${route.label}`, "Outbound · " + (route.sub || "")) : ""}
                      ${daySections}
                      ${returnRoute?.rawRoute ? routeSection(returnRoute.rawRoute, `${returnRoute.icon || "🔄"} ${returnRoute.label}`, "Return · " + (returnRoute.sub || "")) : ""}
                      ${costHtml}

                      <div style="font-size:10px;color:#ccc;text-align:center;margin-top:16px">
                        AI-powered itinerary · Not financial or travel advice · routechef.com
                      </div>
                    </body></html>`;

                      const win = window.open("", "_blank");
                      win.document.write(html);
                      win.document.close();
                      win.onload = () => { win.focus(); win.print(); };
                      setTimeout(() => { try { win.focus(); win.print(); } catch (e) { } }, 800);
                    }}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "7px 14px",
                      background: "linear-gradient(135deg,#F4560A,#F7931E)",
                      border: "none", borderRadius: 99, cursor: "pointer",
                      fontFamily: "inherit", fontWeight: 700, fontSize: 12, color: "#fff",
                      boxShadow: "0 3px 12px rgba(244,86,10,0.35)",
                    }}>
                    <span>⬇️</span> Download PDF
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {days.map((day, di) => {
                    const isExp = expandDay === di;
                    const visits = day.place_visits || [];
                    return (
                      <div key={di} style={{
                        background: "#fff", borderRadius: 16,
                        border: "1px solid #F0EDE8", overflow: "hidden",
                        boxShadow: "0 2px 12px rgba(244,86,10,0.06)",
                      }}>
                        {/* Day header */}
                        <button onClick={() => setExpandDay(isExp ? null : di)} style={{
                          width: "100%", padding: "14px 16px",
                          background: isExp ? "linear-gradient(135deg,#FFF4EE,#FFF8F0)" : "#fff",
                          border: "none", cursor: "pointer",
                          fontFamily: "inherit", display: "flex", alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom: isExp ? "1px solid #F0EDE8" : "none",
                          transition: "background 0.2s",
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            {/* Day number badge */}
                            <div style={{
                              width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                              background: "linear-gradient(135deg,#F4560A,#F7931E)",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontWeight: 900, fontSize: 15, color: "#fff",
                              boxShadow: "0 3px 8px rgba(244,86,10,0.35)",
                            }}>
                              {di + 1}
                            </div>
                            <div style={{ textAlign: "left" }}>
                              <div style={{ fontWeight: 700, fontSize: 13, color: "#111" }}>
                                Day {day.day_number}
                                {day.area_summary && <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>· {day.area_summary}</span>}
                              </div>
                              <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>
                                {visits.length} place{visits.length !== 1 ? "s" : ""}
                                {visits.length > 0 && ` · ${visits[0].start_time || ""} – ${visits[visits.length - 1].end_time || ""}`}
                              </div>
                            </div>
                          </div>
                          <span style={{
                            color: "#E8450A", fontSize: 16,
                            display: "inline-block", transform: isExp ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s"
                          }}>▾</span>
                        </button>

                        {/* Expanded visits */}
                        {isExp && (
                          <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                            {visits.length === 0 && (
                              <div style={{ fontSize: 12, color: "#bbb", textAlign: "center", padding: "8px 0" }}>
                                Free day — explore at your own pace
                              </div>
                            )}
                            {visits.map((v, vi) => {
                              const place = v.place || {};
                              const isLast = vi === visits.length - 1;
                              return (
                                <div key={vi} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                                  {/* Timeline dot + line */}
                                  <div style={{
                                    display: "flex", flexDirection: "column", alignItems: "center",
                                    width: 20, flexShrink: 0, paddingTop: 3
                                  }}>
                                    <div style={{
                                      width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                                      background: "linear-gradient(135deg,#F4560A,#F7931E)",
                                      boxShadow: "0 0 0 3px rgba(244,86,10,0.15)",
                                    }} />
                                    {!isLast && <div style={{
                                      width: 2, flex: 1, minHeight: 16,
                                      background: "linear-gradient(to bottom,#E8450A33,transparent)",
                                      margin: "4px 0"
                                    }} />}
                                  </div>
                                  {/* Place card */}
                                  <div style={{
                                    flex: 1, background: "#FAFAF8", borderRadius: 12,
                                    padding: "10px 13px",
                                    border: "1px solid #F0EDE8",
                                  }}>
                                    <div style={{
                                      display: "flex", alignItems: "flex-start",
                                      justifyContent: "space-between", gap: 8
                                    }}>
                                      <div style={{ fontWeight: 700, fontSize: 13, color: "#111", lineHeight: 1.3 }}>
                                        {place.name || v.place_id}
                                      </div>
                                      {(v.start_time || v.end_time) && (
                                        <div style={{
                                          fontSize: 10, color: "#fff", flexShrink: 0,
                                          background: "linear-gradient(135deg,#F4560A,#F7931E)",
                                          borderRadius: 99, padding: "2px 9px", fontWeight: 700,
                                          whiteSpace: "nowrap"
                                        }}>
                                          {v.start_time}{v.end_time && `–${v.end_time}`}
                                        </div>
                                      )}
                                    </div>
                                    {place.type && (
                                      <div style={{ fontSize: 10, color: ORANGE, marginTop: 3, fontWeight: 600 }}>
                                        {place.type}
                                      </div>
                                    )}
                                    {v.travel_from_prev && v.travel_from_prev !== "Start of day" && (
                                      <div style={{
                                        fontSize: 10, color: "#bbb", marginTop: 3,
                                        display: "flex", alignItems: "center", gap: 4
                                      }}>
                                        <span>🚗</span>{v.travel_from_prev}
                                      </div>
                                    )}
                                    {place.insider_tip && (
                                      <div style={{
                                        fontSize: 10, color: "#555", marginTop: 6,
                                        background: "#FFF4EE", borderRadius: 7,
                                        padding: "5px 8px", lineHeight: 1.5,
                                        borderLeft: `3px solid ${ORANGE}`
                                      }}>
                                        💡 {place.insider_tip.length > 100
                                          ? place.insider_tip.slice(0, 100) + "…"
                                          : place.insider_tip}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {day.day_notes && (
                              <div style={{
                                fontSize: 11, color: "#888", padding: "4px 8px",
                                background: "#F8F8F8", borderRadius: 8, marginTop: 2
                              }}>
                                ℹ️ {day.day_notes}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cost summary — warm white card matching TripPlanner redesign */}
            {total > 0 && (
              <div className="fi" style={{
                background: "#fff", borderRadius: 18,
                border: "1px solid #F0EDE8", overflow: "hidden",
                boxShadow: "0 4px 24px rgba(244,86,10,0.08)",
                marginBottom: 24,
              }}>
                <div style={{ height: 4, background: "linear-gradient(90deg,#F4560A,#E8450A,#F7931E)" }} />
                <div style={{ padding: "14px 20px 12px", display: "flex", flexDirection: "column", gap: 9 }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: "#999",
                    letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 2
                  }}>Cost Breakdown</div>
                  {route && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#444", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{route.icon || "🚂"}</span>
                        {route.label} <span style={{ color: "#bbb", fontSize: 11 }}>outbound</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>
                        {routePrice > 0 ? `₹${fmtP(routePrice)}` : "—"}
                      </span>
                    </div>
                  )}
                  {returnRoute && returnRoutePrice > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#444", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>{returnRoute.icon || "🔄"}</span>
                        {returnRoute.label} <span style={{ color: "#bbb", fontSize: 11 }}>return</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>₹{fmtP(returnRoutePrice)}</span>
                    </div>
                  )}
                  {hotel && hTotalFare > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#444", display: "flex", alignItems: "center", gap: 6 }}>
                        <span>🏨</span>
                        {hotel.hotel_name?.split(" ").slice(0, 3).join(" ")} <span style={{ color: "#bbb", fontSize: 11 }}>{nights}n</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#222" }}>₹{fmtP(hTotalFare)}</span>
                    </div>
                  )}
                  {hotel && (hTotalTax + hPlatFee + hGST) > 0 && (
                    <div>
                      <div onClick={() => setTaxOpen(o => !o)}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                        <span style={{ fontSize: 12, color: "#888", display: "flex", alignItems: "center", gap: 6 }}>
                          <span>🧾</span> Taxes &amp; fees
                          <span style={{ fontSize: 10, color: "#bbb" }}>{taxOpen ? "▲" : "▼"}</span>
                        </span>
                        <span style={{ fontSize: 12, color: "#666" }}>₹{fmtP(hTotalTax + hPlatFee + hGST)}</span>
                      </div>
                      {taxOpen && (
                        <div style={{ paddingLeft: 20, display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
                          {hTotalTax > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#aaa" }}>
                              <span>Hotel tax</span><span>₹{fmtP(hTotalTax)}</span>
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#aaa" }}>
                            <span>Platform fee</span><span>₹249</span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#aaa" }}>
                            <span>GST on platform fee (18%)</span><span>₹45</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{
                  margin: "0 16px 16px",
                  background: "linear-gradient(135deg,#FFF4EE 0%,#FFF8F0 100%)",
                  borderRadius: 12, padding: "12px 16px",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  border: "1px solid #FFE0CC"
                }}>
                  <div>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: "#888",
                      letterSpacing: 1, textTransform: "uppercase", marginBottom: 2
                    }}>Estimated Total</div>
                    <div style={{ fontSize: 10, color: "#bbb" }}>per person · excl. activities</div>
                  </div>
                  <div style={{
                    fontWeight: 900, fontSize: 32,
                    background: "linear-gradient(135deg,#F4560A,#F7931E)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", lineHeight: 1
                  }}>
                    ₹{fmtP(total)}
                  </div>
                </div>
              </div>
            )}

            {/* Bottom CTAs */}
            <div className="fi" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <a href={plannerUrl} style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "15px 0",
                background: "linear-gradient(135deg,#F4560A 0%,#F7931E 100%)",
                borderRadius: 14, color: "#fff", fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(244,86,10,0.40)",
              }}>
                ✦ Open in AI Trip Planner
              </a>
              <button onClick={handleCopyLink} style={{
                padding: "13px 0",
                background: copyState === "copied" ? "#E8F7F2" : "#F0EBF9",
                border: `1.5px solid ${copyState === "copied" ? GREEN : "#E8E8E8"}`,
                borderRadius: 14, cursor: copyState === "loading" ? "default" : "pointer",
                fontFamily: "inherit", fontWeight: 600, fontSize: 13,
                color: copyState === "copied" ? GREEN : "#555", transition: "all 0.2s",
              }}>
                {copyState === "loading" ? "⏳ Creating link…" : copyState === "copied" ? "✓ Short link copied!" : "🔗 Copy share link"}
              </button>
              <div style={{ textAlign: "center", fontSize: 11, color: "#bbb", marginTop: 4 }}>
                AI-powered · Not travel or financial advice · routechef.com
              </div>
            </div>


          </div>
        </div>
      </div>

    </>
  );
}
